/**
 * preview-timings — how long PDF preview refreshes actually take.
 *
 * Auto-refresh on the PDF tab was turned off on 2026-05-14 because it "was
 * burning typst compile cycles and felt jittery". It is back on now that the
 * server caches on a hash of the generated source, but "near instant" is a
 * claim, and the point of this module is to replace it with a distribution.
 *
 * Deliberately local and temporary: a ring buffer in localStorage, read from
 * the console during authoring, thrown away once the question is settled. It
 * is not analytics — nothing leaves the machine, and no identifier is stored.
 *
 * ```js
 * // in the browser console, after authoring for a while
 * JSON.parse(localStorage["lilia.preview-timings"])          // raw entries
 * ```
 *
 * Every function here swallows its own errors. Instrumentation that can break
 * the thing it measures is worse than no instrumentation — a full disk quota
 * must not take the preview down with it.
 */

const STORAGE_KEY = "lilia.preview-timings";

/**
 * Enough to cover a long authoring session while staying far inside the ~5MB
 * localStorage budget — 300 entries is roughly 30KB.
 */
const MAX_ENTRIES = 300;

export type PreviewTrigger = "mount" | "manual" | "auto";

export interface PreviewTiming {
  /** ISO timestamp — lets a session be sliced by time when reading back. */
  at: string;
  /** Wall-clock milliseconds, request start to blob in hand. */
  ms: number;
  /** What caused this refresh. The auto-vs-manual split is the whole question. */
  trigger: PreviewTrigger;
  /** X-Render-Engine, when the server sent one. */
  engine?: string | null;
  /** X-Typst-Cache: hit | miss | not-modified, when present. */
  cache?: string | null;
  /** Response size, as a sanity check that a fast response was not an empty one. */
  bytes?: number;
}

function read(): PreviewTiming[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    // Anything could be under this key — another tab, an older shape, a user
    // poking at it. Treat a surprise as "no data" rather than throwing into
    // the render path.
    return Array.isArray(parsed) ? (parsed as PreviewTiming[]) : [];
  } catch {
    return [];
  }
}

/** Append one measurement, oldest dropped once the buffer is full. */
export function recordPreviewTiming(entry: PreviewTiming): void {
  try {
    const entries = read();
    entries.push(entry);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(entries.slice(-MAX_ENTRIES)),
    );
  } catch {
    // Quota exceeded, private mode, storage disabled. Losing a measurement is
    // acceptable; losing the preview is not.
  }
}

export function readPreviewTimings(): PreviewTiming[] {
  return read();
}

export function clearPreviewTimings(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

export interface PreviewTimingSummary {
  count: number;
  p50: number;
  p95: number;
  max: number;
  /** Counts per trigger, so auto-refresh can be judged on its own numbers. */
  byTrigger: Record<PreviewTrigger, number>;
  /** Counts per cache disposition — how often a refresh cost nothing. */
  byCache: Record<string, number>;
}

/**
 * Percentiles rather than a mean. A mean hides the case that actually decides
 * this: mostly instant with an occasional multi-second stall is what "felt
 * jittery" describes, and an average would report it as fine.
 */
export function summarisePreviewTimings(
  entries: PreviewTiming[] = read(),
): PreviewTimingSummary {
  const byTrigger: Record<PreviewTrigger, number> = { mount: 0, manual: 0, auto: 0 };
  const byCache: Record<string, number> = {};

  for (const e of entries) {
    if (e.trigger in byTrigger) byTrigger[e.trigger]++;
    const key = e.cache ?? "unknown";
    byCache[key] = (byCache[key] ?? 0) + 1;
  }

  if (entries.length === 0) {
    return { count: 0, p50: 0, p95: 0, max: 0, byTrigger, byCache };
  }

  const sorted = entries.map((e) => e.ms).sort((a, b) => a - b);
  const at = (q: number) =>
    sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];

  return {
    count: entries.length,
    p50: at(0.5),
    p95: at(0.95),
    max: sorted[sorted.length - 1],
    byTrigger,
    byCache,
  };
}
